import { useState, useEffect, useMemo } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import useTemplateManifest from '../hooks/use-template-manifest';
import SearchBar from '../components/search-bar';
import KitCard from '../components/kit-card';
import PagePreview from './page-preview';
import SkeletonLoader from '../components/skeleton-loader';
import CustomSelect from '../components/custom-select';

import { getFavorites, toggleFavorite } from '../utils/favorites';
import { highlightText } from '../utils/highlight';
import { sortTemplates } from '../utils/sort-templates';
import {  SORT_OPTIONS} from '../constants/pages';

const KitsPage = ({ onInsertComplete,isProActive,handleUpgradeClick }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [activeKit, setActiveKit] = useState(null);
	const [favorites, setFavorites] = useState([]);
	const [error, setError] = useState(null);
	const [sortBy, setSortBy] = useState('default');

	const {
		templates,
		loading,
		hasMore,
		loadMore,
		total
	} = useTemplateManifest('kits', null, searchQuery, setError);

	useEffect(() => {
		getFavorites().then(setFavorites);
	}, []);

	const handleToggleFavorite = async (id) => {
		const updated = await toggleFavorite(id);
		setFavorites(updated);
	};

	const handleKitClick = (kit) => {
		const normalized = (kit.templates || [])
			.map((tpl) =>
				typeof tpl === 'object' ? tpl : templates.find((t) => t.id === tpl)
			)
			.filter(Boolean);

		setActiveKit({ ...kit, templates: normalized });
	};

	const renderError = () => <p className="vayu-error-state">{error}</p>;

	const renderEmpty = () => (
		<p className={`vayu-empty-state ${searchQuery ? 'has-query' : ''}`}>
			{searchQuery ? (
				<>
					<span className="vayu-empty-state__icon">🔍</span>
					{__('No results found for', 'vayu-blocks')}
					<span className="vayu-empty-state__highlight">"{searchQuery}"</span>
				</>
			) : (
				<>
					<span className="vayu-empty-state__icon">📁</span>
					{__('No templates available here yet.', 'vayu-blocks')}
				</>
			)}
		</p>
	);

	//  Sort with useMemo
	const visibleTemplates = useMemo(() => {
		const unique = templates.filter(
			(tpl, i, self) => self.findIndex((s) => s.id === tpl.id) === i
		);
		return sortTemplates(unique, sortBy);
	}, [templates, sortBy]);

	const renderTemplates = () => (
		<>
			<div className="vayu-template-toolbar">
				{total > 0 && (
					<div className="vayu-dl-template-count">
						{`Showing ${visibleTemplates.length} of ${total} templates`}
					</div>
				)}

			</div>

			<div className={`vayu-template-grid ${ visibleTemplates.length > 0 ? 'fade-in-ready' : ''}`}>
				{visibleTemplates.map((tpl, index) => (
					<KitCard
						key={tpl.id}
						title={
							<span
								dangerouslySetInnerHTML={{
									__html: highlightText(tpl.title, searchQuery),
								}}
							/>
						}
						style={{ '--index': index }}
						pattern={tpl.pattern}
						isFavorite={favorites.includes(tpl.id)}
						onToggleFavorite={() => handleToggleFavorite(tpl.id)}
						templateCount={tpl.templates?.length || 0}
						onClick={() => handleKitClick(tpl)}
						premium={tpl.premium}
						isProActive={isProActive}
						handleUpgradeClick={handleUpgradeClick}
					/>
				))}
			</div>

			{hasMore && total > templates.length && (
				<div className="vayu-load-more-wrap  vb-kits-button-loadmore">
					<button
						className="vayu-load-more-button"
						onClick={loadMore}
						disabled={loading}
					>
						{loading ? __('Loading...', 'vayu-blocks') : __('Load More', 'vayu-blocks')}
					</button>
				</div>
			)}
		</>
	);

	return (
		<>
			{activeKit ? (
				<PagePreview
					kit={activeKit}
					onBack={() => setActiveKit(null)}
					onInsertComplete={onInsertComplete}
					isProActive={isProActive}
					handleUpgradeClick={handleUpgradeClick}
				/>
			) : (
				<div className="vb-content-kits-page">
					<div className="vayu-page-toolbar vayu-favorites-toolbar vb-kites-sizze">
						<h2>{__('Template', 'vayu-blocks')}</h2>
						<div className="vayu-toolbar-right">
							{visibleTemplates.length > 1 && (
								<CustomSelect
									value={sortBy}
									onChange={setSortBy}
									options={SORT_OPTIONS}
								/>
							)}
							<SearchBar
								onSearch={setSearchQuery}
								activeTab="kits"
								className="vayu-search-bar"
							/>
						</div>
					</div>

					<section className="vayu-main-content">
						{loading && templates.length === 0
							? <SkeletonLoader count={6} />
							: error
							? renderError()
							: visibleTemplates.length === 0
							? renderEmpty()
							: renderTemplates()}
					</section>
				</div>
			)}
		</>
	);
};

export default KitsPage;